import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import {
  authConfig, completeSignInLink, describeFirebaseError, isSignInLink,
  registerWithPassword, rememberedLinkEmail, sendPasswordReset,
  sendSignInLink, signInWithPassword, signInWithProvider,
} from '../lib/firebase';
import type { AuthConfig } from '../lib/firebase';
import { BRAND_NAME, BrandMark } from '../components/Layout';
import { Banner, Field, Loading } from '../components/ui';
import { useSession } from '../state/session';
import { useToast } from '../state/toast';

const PROVIDER_LABELS: Record<string, { label: string; mark: string }> = {
  google: { label: 'Continue with Google', mark: 'G' },
  'google.com': { label: 'Continue with Google', mark: 'G' },
  microsoft: { label: 'Continue with Microsoft', mark: 'M' },
  'microsoft.com': { label: 'Continue with Microsoft', mark: 'M' },
  apple: { label: 'Continue with Apple', mark: '' },
  'apple.com': { label: 'Continue with Apple', mark: '' },
};

/**
 * Decorative mark for the auth panel: a document whose signed line is carried
 * into a chain of linked blocks.
 *
 * It draws what the product is actually for — a signature and the record that
 * outlives it — rather than a generic padlock. Purely decorative, so it is
 * hidden from assistive technology.
 */
function AuthGraphic() {
  return (
    <svg viewBox="0 0 420 360" fill="none" className="authpane__art"
         aria-hidden="true">
      <defs>
        <radialGradient id="esl-glow-a" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#5b8cff" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#5b8cff" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="esl-glow-b" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#36d399" stopOpacity="0.36" />
          <stop offset="100%" stopColor="#36d399" stopOpacity="0" />
        </radialGradient>
      </defs>

      <circle cx="140" cy="110" r="185" fill="url(#esl-glow-a)" />
      <circle cx="320" cy="290" r="150" fill="url(#esl-glow-b)" />

      {/* the document */}
      <rect x="96" y="52" width="196" height="238" rx="16"
            stroke="#8fb4ff" strokeWidth="2.5" opacity="0.95" />
      <g stroke="#8fb4ff" strokeWidth="2" strokeLinecap="round" opacity="0.4">
        <line x1="126" y1="98" x2="248" y2="98" />
        <line x1="126" y1="122" x2="262" y2="122" />
        <line x1="126" y1="146" x2="226" y2="146" />
        <line x1="126" y1="170" x2="254" y2="170" />
      </g>

      {/* the signature, and the rule it sits on */}
      <path d="M128 232c22-6 30-56 48-56 15 0 10 42 26 42 13 0 18-24 30-24"
            stroke="#ffffff" strokeWidth="3.2" strokeLinecap="round"
            strokeLinejoin="round" />
      <line x1="126" y1="252" x2="262" y2="252"
            stroke="#8fb4ff" strokeWidth="2" strokeLinecap="round" opacity="0.55" />

      {/* the evidence chain carrying it forward */}
      <g opacity="0.95">
        <rect x="262" y="286" width="34" height="34" rx="9"
              stroke="#36d399" strokeWidth="2.5" />
        <rect x="312" y="286" width="34" height="34" rx="9"
              stroke="#36d399" strokeWidth="2.5" />
        <rect x="362" y="286" width="34" height="34" rx="9"
              stroke="#36d399" strokeWidth="2.5" opacity="0.5" />
        <line x1="296" y1="303" x2="312" y2="303"
              stroke="#36d399" strokeWidth="2.5" strokeLinecap="round" />
        <line x1="346" y1="303" x2="362" y2="303"
              stroke="#36d399" strokeWidth="2.5" strokeLinecap="round" opacity="0.5" />
      </g>
      <path d="M254 268 C 262 280, 262 284, 266 288"
            stroke="#36d399" strokeWidth="2" strokeLinecap="round"
            strokeDasharray="1 7" opacity="0.8" />

      {/* a tick, because the point is that it checks out */}
      <circle cx="279" cy="303" r="7" fill="none" />
      <path d="M272 303l5 5 9-11" stroke="#36d399" strokeWidth="2.6"
            strokeLinecap="round" strokeLinejoin="round" />

      <g stroke="#8fb4ff" strokeWidth="2" strokeLinecap="round" opacity="0.6">
        <line x1="72" y1="88" x2="72" y2="104" />
        <line x1="64" y1="96" x2="80" y2="96" />
      </g>
      <circle cx="330" cy="92" r="4" fill="#8fb4ff" opacity="0.75" />
      <circle cx="348" cy="138" r="6" stroke="#8fb4ff" strokeWidth="2" opacity="0.5" />
    </svg>
  );
}

function AuthFrame({ title, lede, children, footer, aside }: {
  title: string; lede: string; children: React.ReactNode;
  footer: React.ReactNode; aside?: { headline: string; sub: string };
}) {
  return (
    <div className="authsplit">
      {/* Brand panel. Hidden below the split breakpoint, where the form should
          have the whole screen rather than compete with decoration. */}
      <aside className="authpane">
        <Link className="authpane__brand" to="/" aria-label={BRAND_NAME}>
          <BrandMark size={30} />
          <span>{BRAND_NAME}</span>
        </Link>
        <div className="authpane__figure"><AuthGraphic /></div>
        <div className="authpane__copy">
          <h2>{aside?.headline ?? 'Signatures you can actually prove.'}</h2>
          <p>
            {aside?.sub
              ?? 'Every completed agreement comes with the exact document that '
               + 'was signed, a SHA-256 record of its bytes, and an append-only '
               + 'audit trail anyone can check — including after you cancel.'}
          </p>
        </div>
      </aside>

      <section className="authform">
        <div className="authform__inner stack">
          <Link className="authform__brand" to="/" aria-label={BRAND_NAME}>
            <BrandMark size={28} />
            <span>{BRAND_NAME}</span>
          </Link>
          <header className="stack-s">
            <h1 style={{ fontSize: '1.5rem', margin: 0 }}>{title}</h1>
            <p className="muted small" style={{ margin: 0 }}>{lede}</p>
          </header>
          {children}
          <hr className="divider" />
          <p className="small muted" style={{ margin: 0 }}>{footer}</p>
          <p className="small muted authform__legal" style={{ margin: 0 }}>
            <Link to="/terms">Terms</Link> · <Link to="/privacy">Privacy</Link> ·{' '}
            <Link to="/support">Support</Link>
          </p>
        </div>
      </section>
    </div>
  );
}

/** Federated sign-in buttons, shown when Firebase offers those providers. */
function ProviderButtons({ config, organizationName, onDone, disabled, terms }: {
  config: AuthConfig; organizationName?: string;
  onDone: (result: any) => void; disabled?: boolean;
  /** Present on sign-up, where these buttons create the account. */
  terms?: { accepted: boolean; version: string };
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Set when the provider turned out to be creating an account rather than
  // signing in to one, and the terms have not been accepted yet.
  const [needsTerms, setNeedsTerms] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);

  const providers = config.firebase.providers.filter((p) => p !== 'password');
  if (!config.firebase.enabled || providers.length === 0) return null;

  const start = async (providerId: string, accepting = false) => {
    if (terms && !terms.accepted) {
      setError('Accept the Terms of Service and Privacy Policy first.');
      return;
    }
    setBusy(providerId); setError(null);
    try {
      const carried = terms
        ?? (accepting ? { accepted: true, version: config.terms_version } : undefined);
      onDone(await signInWithProvider(providerId, organizationName, carried));
      setNeedsTerms(null);
    } catch (cause) {
      // Signing in with a provider creates the account when there is not one
      // yet, so a "sign in" can turn out to be a sign-up. Rather than failing
      // with a rule they were never shown, ask here and carry on.
      if (cause instanceof ApiError
          && (cause.code === 'terms_acceptance_required' || cause.code === 'stale_terms')) {
        setNeedsTerms(providerId);
        setError(null);
      } else {
        setError(cause instanceof ApiError ? cause.message : describeFirebaseError(cause));
      }
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="stack-s">
      {error && <Banner tone="danger" title="Could not sign in">{error}</Banner>}
      {needsTerms && (
        <Banner tone="info" title="One moment — you are creating an account">
          <label className="checkbox" style={{ marginTop: 6 }}>
            <input type="checkbox" checked={accepted}
                   onChange={(event) => setAccepted(event.target.checked)} />
            <span className="checkbox__text">
              I agree to the{' '}
              <Link to="/terms" target="_blank" rel="noopener">Terms of Service</Link>
              {' '}and the{' '}
              <Link to="/privacy" target="_blank" rel="noopener">Privacy Policy</Link>.
            </span>
          </label>
          <button type="button" className="btn btn--primary btn--small"
                  style={{ marginTop: 10 }}
                  disabled={!accepted || busy !== null}
                  onClick={() => void start(needsTerms, true)}>
            Accept and continue
          </button>
        </Banner>
      )}
      {providers.filter((id) => id !== 'email_link').map((providerId) => {
        const meta = PROVIDER_LABELS[providerId]
          || { label: `Continue with ${providerId}`, mark: '◆' };
        return (
          <button key={providerId} type="button" className="btn btn--block btn--large"
                  disabled={disabled || busy !== null}
                  onClick={() => void start(providerId)}>
            <span aria-hidden="true" style={{ fontWeight: 700 }}>{meta.mark}</span>
            {busy === providerId ? 'Opening…' : meta.label}
          </button>
        );
      })}
      {config.firebase.email_password_enabled && (
        <div className="row" style={{ gap: 10, alignItems: 'center' }}>
          <hr style={{ flex: 1, border: 0, borderTop: '1px solid var(--line)' }} />
          <span className="small muted">or</span>
          <hr style={{ flex: 1, border: 0, borderTop: '1px solid var(--line)' }} />
        </div>
      )}
    </div>
  );
}

/**
 * Passwordless sign-in.
 *
 * Two states in one component: ask for the address and send a link, or — when
 * the browser has arrived back on a link — complete the exchange. Completing
 * needs the address the link was sent to, which is normally remembered from
 * sending; a link opened on a different device asks for it again rather than
 * failing.
 */
function EmailLinkSignIn({ organizationName, onDone }: {
  organizationName?: string;
  onDone: (result: unknown) => void;
}) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [needsEmail, setNeedsEmail] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Arriving back on a link: finish immediately if this browser remembers the
  // address, otherwise ask for it.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!(await isSignInLink())) return;
      if (cancelled) return;
      setCompleting(true);
      const remembered = rememberedLinkEmail();
      if (!remembered?.email) {
        setNeedsEmail(true);
        return;
      }
      try {
        onDone(await completeSignInLink(remembered.email,
                                        remembered.organizationName || undefined));
      } catch (cause) {
        setError(cause instanceof ApiError ? cause.message
                                           : describeFirebaseError(cause));
        setNeedsEmail(true);
      }
    })();
    return () => { cancelled = true; };
  }, [onDone]);

  const send = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true); setError(null);
    try {
      await sendSignInLink(email.trim(), organizationName);
      setSent(true);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message
                                         : describeFirebaseError(cause));
    } finally { setBusy(false); }
  };

  const complete = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true); setError(null);
    try {
      onDone(await completeSignInLink(email.trim(), organizationName));
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message
                                         : describeFirebaseError(cause));
      setBusy(false);
    }
  };

  if (completing) {
    return (
      <div className="stack-s">
        {error && <Banner tone="danger" title="Could not sign in">{error}</Banner>}
        {needsEmail ? (
          <form className="stack-s" onSubmit={complete}>
            <p className="small muted" style={{ margin: 0 }}>
              Confirm the address this link was sent to. Links are single use
              and only work for the address they were issued for.
            </p>
            <Field label="Email" id="link-confirm-email" required>
              <input id="link-confirm-email" className="input" type="email"
                     required autoComplete="email" value={email}
                     onChange={(event) => setEmail(event.target.value)} />
            </Field>
            <button className="btn btn--primary btn--block" disabled={busy}>
              {busy ? 'Signing in…' : 'Finish signing in'}
            </button>
          </form>
        ) : (
          <Loading label="Signing you in…" />
        )}
      </div>
    );
  }

  if (sent) {
    return (
      <Banner tone="ok" title="Check your email">
        We sent a sign-in link to <strong>{email}</strong>. It works once, and
        only on this device unless you confirm the address again. If it has not
        arrived in a minute, check the junk folder.
      </Banner>
    );
  }

  return (
    <form className="stack-s" onSubmit={send}>
      {error && <Banner tone="danger" title="Could not send the link">{error}</Banner>}
      <Field label="Email" id="link-email" required
             hint="We email you a link. No password to remember.">
        <input id="link-email" className="input" type="email" required
               autoComplete="email" value={email}
               onChange={(event) => setEmail(event.target.value)} />
      </Field>
      <button className="btn btn--block" disabled={busy}>
        {busy ? 'Sending…' : 'Email me a sign-in link'}
      </button>
    </form>
  );
}

export function Register() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useSession();
  const { notify } = useToast();
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [form, setForm] = useState({
    name: '', email: '', organization_name: '', password: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  useEffect(() => { authConfig().then(setConfig); }, []);

  // Carried into every route that can create the account, including the
  // federated ones. The server requires it and stores the version; this is
  // only how the answer reaches it.
  const terms = { accepted: acceptedTerms, version: config?.terms_version || '' };

  const finish = async (result: any) => {
    await refresh();
    if (result?.claimed_preparation?.documents?.length) {
      notify('Your prepared document was saved to your account.', 'ok');
    }
    navigate(params.get('next') === 'send' ? '/app/new' : '/app');
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true); setError(null);
    try {
      const result = config?.firebase.enabled
        ? await registerWithPassword(form.email, form.password, form.name,
                                     form.organization_name, terms)
        : await api.post('/auth/register', {
            ...form,
            accepted_terms: acceptedTerms,
            terms_version: terms.version,
          });
      await finish(result);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : describeFirebaseError(cause));
      setBusy(false);
    }
  };

  if (!config) {
    return (
      <AuthFrame title="Create your account" lede="One moment…" footer={null}>
        <Loading />
      </AuthFrame>
    );
  }

  // In Firebase mode the email/password form belongs to Firebase; the local
  // setting only governs this platform's own password accounts.
  const passwordAllowed = config.firebase.enabled
    ? config.firebase.email_password_enabled
    : config.registration_enabled;

  return (
    <AuthFrame
      title="Create your account"
      lede="Free to create. A card is needed only when you activate the sending trial."
      footer={<>Already have an account? <Link to="/login">Sign in</Link>.</>}
      aside={{
        headline: 'Start sending in a couple of minutes.',
        sub: 'Prepare an agreement, send it for signature, and get back a '
           + 'record that stands on its own. No card until you send, and the '
           + 'three-day trial includes two signature requests.',
      }}
    >
      {error && <Banner tone="danger" title="Check these details">{error}</Banner>}

      {/* Above the provider buttons as well as the form, because both of them
          create the account. The server refuses either way without it. */}
      <label className="checkbox">
        <input type="checkbox" checked={acceptedTerms}
               onChange={(event) => setAcceptedTerms(event.target.checked)} />
        <span className="checkbox__text">
          I agree to the{' '}
          <Link to="/terms" target="_blank" rel="noopener">Terms of Service</Link>
          {' '}and the{' '}
          <Link to="/privacy" target="_blank" rel="noopener">Privacy Policy</Link>.
        </span>
      </label>

      <ProviderButtons config={config} organizationName={form.organization_name}
                       onDone={finish} disabled={busy} terms={terms} />

      {passwordAllowed ? (
        <form className="stack" onSubmit={submit}>
          <Field label="Your name" id="reg-name" required>
            <input id="reg-name" className="input" required autoComplete="name"
                   value={form.name}
                   onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label="Work email" id="reg-email" required>
            <input id="reg-email" className="input" type="email" required
                   autoComplete="email" inputMode="email" value={form.email}
                   onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
          <Field label="Organization name" id="reg-org"
                 hint="You can change this later in settings.">
            <input id="reg-org" className="input" autoComplete="organization"
                   value={form.organization_name}
                   onChange={(e) => setForm({ ...form, organization_name: e.target.value })} />
          </Field>
          <Field label="Password" id="reg-pass" required hint="At least 10 characters.">
            <input id="reg-pass" className="input" type="password" required minLength={10}
                   autoComplete="new-password" value={form.password}
                   onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </Field>
          <button className="btn btn--primary btn--block btn--large"
                  disabled={busy || !acceptedTerms}>
            {busy ? 'Creating…' : 'Create account'}
          </button>
        </form>
      ) : (
        <p className="small muted" style={{ margin: 0 }}>
          This deployment uses the sign-in providers above.
        </p>
      )}
    </AuthFrame>
  );
}

export function Login() {
  const navigate = useNavigate();
  const { refresh } = useSession();
  const { notify } = useToast();
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [form, setForm] = useState({ email: '', password: '', mfa_code: '' });
  const [needsMfa, setNeedsMfa] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { authConfig().then(setConfig); }, []);

  const finish = async () => { await refresh(); navigate('/app'); };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true); setError(null);
    try {
      if (config?.firebase.enabled) {
        await signInWithPassword(form.email, form.password);
      } else {
        await api.post('/auth/login', {
          email: form.email, password: form.password,
          mfa_code: form.mfa_code || undefined,
        });
      }
      await finish();
    } catch (cause) {
      if (cause instanceof ApiError && cause.code === 'mfa_required') {
        setNeedsMfa(true);
        setError(null);
      } else {
        setError(cause instanceof ApiError ? cause.message
                                           : describeFirebaseError(cause));
      }
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!form.email) { setError('Enter your email address first.'); return; }
    try {
      await sendPasswordReset(form.email);
      notify('If that address has an account, a reset email is on its way.', 'ok');
    } catch (cause) {
      setError(describeFirebaseError(cause));
    }
  };

  if (!config) {
    return <AuthFrame title="Sign in" lede="One moment…" footer={null}><Loading /></AuthFrame>;
  }

  const passwordAllowed = config.firebase.enabled
    ? config.firebase.email_password_enabled
    : config.password_sign_in_enabled;
  // Passwordless links need Firebase; there is no local equivalent.
  const linkAllowed = config.firebase.enabled
    && config.firebase.providers.includes('email_link');

  return (
    <AuthFrame
      title="Sign in"
      lede="Access your agreements, evidence and billing."
      footer={<>New here? <Link to="/register">Create an account</Link>, or{' '}
        <Link to="/prepare">prepare a document first</Link>.</>}
      aside={{
        headline: 'Signatures you can actually prove.',
        sub: 'Every completed agreement comes with the exact document that was '
           + 'signed, a SHA-256 record of its bytes, and an append-only audit '
           + 'trail anyone can check — including after you cancel.',
      }}
    >
      {error && <Banner tone="danger" title="Could not sign in">{error}</Banner>}

      <ProviderButtons config={config} onDone={finish} disabled={busy} />

      {linkAllowed && (
        <>
          <EmailLinkSignIn onDone={finish} />
          {passwordAllowed && (
            <div className="row" style={{ gap: 10, alignItems: 'center' }}>
              <hr style={{ flex: 1, border: 0, borderTop: '1px solid var(--line)' }} />
              <span className="small muted">or use a password</span>
              <hr style={{ flex: 1, border: 0, borderTop: '1px solid var(--line)' }} />
            </div>
          )}
        </>
      )}

      {passwordAllowed ? (
        <form className="stack" onSubmit={submit}>
          <Field label="Email" id="login-email" required>
            <input id="login-email" className="input" type="email" required
                   autoComplete="email" inputMode="email" value={form.email}
                   onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
          <Field label="Password" id="login-pass" required>
            <input id="login-pass" className="input" type="password" required
                   autoComplete="current-password" value={form.password}
                   onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </Field>
          {needsMfa && (
            <Field label="Authenticator code" id="login-mfa" required
                   hint="Six digits from your authenticator app.">
              <input id="login-mfa" className="input" inputMode="numeric"
                     autoComplete="one-time-code" maxLength={6} required
                     value={form.mfa_code}
                     onChange={(e) => setForm({ ...form, mfa_code: e.target.value })} />
            </Field>
          )}
          <button className="btn btn--primary btn--block btn--large" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
          {config.firebase.enabled && (
            <button type="button" className="btn btn--ghost btn--block"
                    onClick={() => void reset()}>
              Forgot your password?
            </button>
          )}
        </form>
      ) : (
        <p className="small muted" style={{ margin: 0 }}>
          This deployment uses the sign-in providers above.
        </p>
      )}
    </AuthFrame>
  );
}
